import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface EmblaCarouselProps {
  slides: React.ReactNode[];
  autoplay?: boolean;
  interval?: number;
  border?: boolean;
  startIndex?: number;
}

const EmblaCarousel: React.FC<EmblaCarouselProps> = ({
  slides,
  autoplay = false,
  interval = 4000,
  border,
  startIndex = 0,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    startIndex: startIndex,
    watchDrag: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(startIndex);

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!autoplay || !emblaApi) return;

    const timer = setInterval(() => {
      emblaApi.scrollNext();
    }, interval);

    return () => clearInterval(timer);
  }, [emblaApi, autoplay, interval]);

  return (
    <div
      className={`relative w-full h-full overflow-hidden rounded-lg ${
        border ? "border border-gray-300" : ""
      }`}
    >
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => (
            <div key={index} className="min-w-full h-full">
              {React.cloneElement(slide as React.ReactElement, {
                className: "w-full h-full object-contain",
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-1 right-2 left-2 flex items-center justify-between">
        {/* Arrow Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={scrollPrev}
            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center bg-transparent hover:bg-gray-100 transition"
            aria-label="Previous slide"
            disabled={slides.length <= 1}
          >
            <FiChevronLeft className="w-5 h-5 text-black" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center bg-transparent hover:bg-gray-100 transition"
            aria-label="Next slide"
            disabled={slides.length <= 1}
          >
            <FiChevronRight className="w-5 h-5 text-black" />
          </button>
        </div>

        {/* Navigation Dots */}
        <div className="flex items-center gap-1">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollTo(index)}
              className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${
                selectedIndex === index ? "bg-[#48455B]" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmblaCarousel;
